import { brand } from '@enp/brand';
import type { ImportConnection, ImportRunSummary } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import {
  Badge,
  Button,
  createColumnHelper,
  DataTable,
  EmptyState,
  PageHeader,
  Skeleton,
  type ColumnDef,
} from '@enp/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  CircleCheck,
  History,
  Loader2,
  TriangleAlert,
  Play,
  Plug,
  PlugZap,
  Settings2,
  ShieldCheck,
} from 'lucide-react';
import type { ReactNode } from 'react';
import {
  CheckStatusBadge,
  isOutdated,
  Note,
  RunStatusBadge,
  Section,
  useImportPermissions,
  useStartRun,
} from '../../../imports/components.js';
import {
  connectionsQuery,
  connectorsQuery,
  isBusy,
  rulesQuery,
  runsQuery,
} from '../../../imports/queries.js';
import { runBadge } from '../../../imports/labels.js';
import { AccountDate } from '../../../shell/account-date.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';

export const Route = createFileRoute('/_app/imports/')({
  component: ImportsOverview,
});

// ---------------------------------------------------------------- sources

function connectionState(c: ImportConnection): {
  label: string;
  tone: 'success' | 'warning' | 'danger';
} {
  if (c.target_confirmed_at && c.mode !== 'off')
    return { label: 'Ready to import', tone: 'success' };
  if (c.target_confirmed_at && c.mode === 'off') {
    return { label: 'Switched off: account didn’t match', tone: 'danger' };
  }
  return { label: 'Waiting for the owner to confirm the account', tone: 'warning' };
}

function ConnectionCard({
  connection,
  busyRun,
  secondary,
}: {
  connection: ImportConnection;
  busyRun: ImportRunSummary | undefined;
  /** Another action leads the page (decisions waiting): starting an import isn't primary. */
  secondary: boolean;
}) {
  const { canEdit } = useImportPermissions();
  const { start, pending } = useStartRun();
  const state = connectionState(connection);
  const ready = state.tone === 'success';
  return (
    <li className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-border bg-surface-raised p-4">
      <span
        aria-hidden
        className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary-subtle text-primary-text"
      >
        <PlugZap className="size-5" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="flex flex-wrap items-center gap-2 font-medium text-fg">
          {connection.display_name}
          {connection.environment === 'test' && <Badge tone="outline">Practice data</Badge>}
        </p>
        <p className="text-sm text-muted">
          {connection.reported_target?.name ? (
            <>
              Account <span className="text-fg">{connection.reported_target.name}</span>
            </>
          ) : (
            'Account not identified yet'
          )}
          {' · '}
          <Badge tone={state.tone}>{state.label}</Badge>
        </p>
        {connection.last_error && !ready && (
          <p className="text-sm text-state-danger">{connection.last_error}</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {!ready && (
          <Button variant={canEdit ? 'primary' : 'secondary'} asChild>
            <Link to="/imports/connect" search={{ connection: connection.id }}>
              <ShieldCheck aria-hidden />
              Check the account
            </Link>
          </Button>
        )}
        {ready &&
          (busyRun ? (
            <Button variant="secondary" asChild>
              <Link to="/imports/runs/$runId" params={{ runId: busyRun.id }}>
                Open the running import
                <ArrowRight aria-hidden />
              </Link>
            </Button>
          ) : (
            canEdit && (
              <Button
                variant={secondary ? 'secondary' : 'primary'}
                icon={<Play aria-hidden />}
                loading={pending}
                onClick={() => start(connection.id)}
              >
                {pending ? 'Starting…' : 'Start an import'}
              </Button>
            )
          ))}
      </div>
    </li>
  );
}

function Sources() {
  const connections = useQuery(connectionsQuery());
  const runs = useInfiniteQuery(runsQuery());
  const { canEdit } = useImportPermissions();
  const latest = runs.data?.pages[0]?.items ?? [];
  const waiting = latest.some((r) => r.status === 'awaiting_review');

  let body: ReactNode;
  if (connections.isPending) {
    body = (
      <div role="status" aria-live="polite" className="flex flex-col gap-3">
        <span className="sr-only">Loading sources…</span>
        <Skeleton className="h-20 w-full rounded-lg" />
      </div>
    );
  } else if (connections.isError) {
    body = (
      <ErrorPanel
        error={connections.error}
        titleAs="h2"
        what="Your sources"
        onRetry={() => void connections.refetch()}
      />
    );
  } else if (connections.data.connections.length === 0) {
    body = (
      <EmptyState
        compact
        icon={Plug}
        title="No source connected yet"
        description={`Connect the till system you’re switching from. ${brand.productName} only reads from it; it never changes anything there.`}
        action={
          canEdit ? (
            <Button asChild>
              <Link to="/imports/connect">Connect a source</Link>
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    body = (
      <ul className="flex flex-col gap-3" aria-label="Connected sources">
        {connections.data.connections.map((c) => (
          <ConnectionCard
            key={c.id}
            connection={c}
            busyRun={latest.find((r) => r.connection_id === c.id && isBusy(r.status))}
            secondary={waiting}
          />
        ))}
      </ul>
    );
  }
  return (
    <Section
      id="sources"
      title="Sources"
      description="Where your catalogue comes from. Every import reads it again, checks it and shows you the result before anything is saved."
    >
      {body}
    </Section>
  );
}

// ---------------------------------------------------------------- history

const col = createColumnHelper<ImportRunSummary>();

function SourceCell({ kind }: { kind: string }) {
  const connectors = useQuery(connectorsQuery());
  return <>{connectors.data?.connectors.find((c) => c.kind === kind)?.display_name ?? kind}</>;
}

/** A history row's badges: out of date when the rules changed after it was checked. */
function RunBadges({ run, kind }: { run: ImportRunSummary; kind: 'status' | 'report' }) {
  const rules = useQuery(rulesQuery());
  const outdated = isOutdated(run, rules.data?.revision);
  if (kind === 'status') return <RunStatusBadge run={run} outdated={outdated} />;
  return run.report_status ? (
    <CheckStatusBadge status={run.report_status} outdated={outdated} />
  ) : (
    <span className="text-muted">Not checked</span>
  );
}

// TanStack's documented DataTable typing: columns carry heterogeneous value types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<ImportRunSummary, any>[] = [
  col.accessor('started_at', {
    header: 'Started',
    cell: ({ row }) => (
      <Link
        to="/imports/runs/$runId"
        params={{ runId: row.original.id }}
        tabIndex={-1}
        className="whitespace-nowrap font-medium text-fg hover:underline"
      >
        <AccountDate value={row.original.started_at} withTime />
      </Link>
    ),
  }),
  col.display({
    id: 'source',
    header: 'Source',
    cell: ({ row }) => <SourceCell kind={row.original.connector_kind} />,
  }),
  col.display({
    id: 'status',
    header: 'Status',
    cell: ({ row }) => <RunBadges run={row.original} kind="status" />,
  }),
  col.display({
    id: 'report',
    header: 'Report',
    cell: ({ row }) => <RunBadges run={row.original} kind="report" />,
  }),
  col.display({
    id: 'review',
    header: 'Decisions needed',
    meta: { align: 'right' },
    cell: ({ row }) =>
      row.original.review_items === null ? (
        <span className="text-muted">—</span>
      ) : (
        <span className="tabular-nums">{formatCount(row.original.review_items)}</span>
      ),
  }),
  col.display({
    id: 'items',
    header: 'Items read',
    meta: { align: 'right', className: 'hidden xl:table-cell' },
    cell: ({ row }) => {
      const n = row.original.counts.extract?.items;
      return n === undefined ? (
        <span className="text-muted">—</span>
      ) : (
        <span className="tabular-nums">{formatCount(n)}</span>
      );
    },
  }),
];

function HistoryTable() {
  const navigate = useNavigate();
  const runs = useInfiniteQuery(runsQuery());
  const items = runs.data?.pages.flatMap((p) => p.items) ?? [];
  if (runs.isError && !runs.data) {
    return (
      <ErrorPanel
        error={runs.error}
        titleAs="h2"
        what="The import history"
        onRetry={() => void runs.refetch()}
      />
    );
  }
  return (
    <DataTable
      label="Import history"
      columns={columns}
      data={items}
      loading={runs.isPending}
      filterable={false}
      getRowId={(r) => r.id}
      onRowActivate={(r) => void navigate({ to: '/imports/runs/$runId', params: { runId: r.id } })}
      empty={
        <EmptyState
          compact
          icon={History}
          title="No imports yet"
          description="Once a source is connected and its account confirmed, start an import. Each one is listed here with its report."
        />
      }
      footer={
        runs.hasNextPage ? (
          <Button
            variant="secondary"
            size="sm"
            loading={runs.isFetchingNextPage}
            onClick={() => void runs.fetchNextPage()}
          >
            {runs.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        ) : items.length > 0 ? (
          <p className="text-sm text-muted">
            {formatCount(items.length)} {items.length === 1 ? 'import' : 'imports'}
          </p>
        ) : undefined
      }
      className="max-h-[32rem]"
    />
  );
}

/** The newest run that still needs someone (review or load), as a call to action. */
function NextStep() {
  const runs = useInfiniteQuery(runsQuery());
  const run = runs.data?.pages[0]?.items.find(
    (r) => r.status === 'awaiting_review' || r.status === 'ready' || isBusy(r.status),
  );
  const rules = useQuery(rulesQuery());
  if (!run) return null;
  // Says what the run's badge says (UI1A-201): "ready" only when a load would be accepted.
  const badge = runBadge({ ...run, outdated: isOutdated(run, rules.data?.revision) });
  let text: string;
  if (run.status === 'awaiting_review') {
    text = `The latest import needs ${formatCount(run.review_items ?? 0)} ${run.review_items === 1 ? 'decision' : 'decisions'} before it can be loaded.`;
  } else if (run.status !== 'ready') {
    text = 'An import is running now.';
  } else if (badge.label === 'Needs re-check') {
    text =
      'The import rules changed after the latest import was checked: check it again before loading.';
  } else if (badge.status === 'danger') {
    text = 'The latest import’s report failed, so it can’t be loaded.';
  } else if (badge.status === 'warning') {
    text =
      'The latest import’s report isn’t a full pass: only the owner can load it, after reading it.';
  } else {
    text = 'The latest import is checked and ready to load into the catalogue.';
  }
  // Decisions waiting are a job for someone: warning, not information (UI1A-12).
  const tone =
    run.status === 'awaiting_review'
      ? 'warning'
      : run.status === 'ready'
        ? badge.status === 'neutral'
          ? 'info'
          : badge.status
        : 'info';
  const icon =
    tone === 'success'
      ? CircleCheck
      : tone === 'info' && run.status !== 'ready'
        ? Loader2
        : TriangleAlert;
  return (
    <Note icon={icon} tone={tone} className="mb-6">
      <p className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span>{text}</span>
        <Link
          to="/imports/runs/$runId"
          params={{ runId: run.id }}
          className="font-medium text-primary-text underline underline-offset-2"
        >
          Open it
        </Link>
      </p>
    </Note>
  );
}

function ImportsOverview() {
  useDocumentTitle('Imports');
  const { canEdit } = useImportPermissions();
  const connections = useQuery(connectionsQuery());
  const runs = useInfiniteQuery(runsQuery());
  // One primary action per page (UI1A-12, UI1A-207): decisions waiting lead; else, until a
  // source is connected, "Connect a source"; then the source's own "Start an import". A
  // business has one connection per source, so "Connect a source" goes once one exists.
  const connected = (connections.data?.connections.length ?? 0) > 0;
  const waiting = runs.data?.pages[0]?.items.find((r) => r.status === 'awaiting_review');
  const decisions = waiting?.review_items ?? 0;
  return (
    <>
      <PageHeader
        title="Imports"
        description={`Switch from your till system: bring in your catalogue, check that every record is accounted for, then load it. ${brand.productName} only reads from the source.`}
        actions={
          <>
            <Button variant="secondary" asChild>
              <Link to="/imports/rules">
                <Settings2 aria-hidden />
                Import rules
              </Link>
            </Button>
            {canEdit && !connected && connections.isSuccess && (
              <Button asChild>
                <Link to="/imports/connect">
                  <Plug aria-hidden />
                  Connect a source
                </Link>
              </Button>
            )}
            {waiting && (
              <Button asChild>
                <Link to="/imports/runs/$runId/review" params={{ runId: waiting.id }}>
                  Decide {formatCount(decisions)} {decisions === 1 ? 'item' : 'items'}
                  <ArrowRight aria-hidden />
                </Link>
              </Button>
            )}
          </>
        }
      />
      <NextStep />
      <div className="flex flex-col gap-6">
        <Sources />
        <Section
          id="history"
          title="History"
          description="Every import with its report. Open one to see what it found."
        >
          <HistoryTable />
        </Section>
      </div>
    </>
  );
}
